import { LinearProgress, Typography } from '@material-ui/core';
import { Stack, useResponsiveValue } from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import { useNavigate } from 'react-router-dom';
import { isWebView } from 'shared/constants/AppTypeConstants';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import { openExternalURL } from 'shared/helpers/URLHelpers';
import {
  Declined,
  PendingVerification,
  Reviewed,
  Revoked,
} from 'shared/modules/carrier-profile/core/VerificationBadge';
import { VerifiedCarrierApplicationStatusDTO } from 'shared/modules/carrier-profile/data/VerifiedCarrierApplicationStatusDTO';
import { LinkButton } from 'shared/routing/Links';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import styled from 'styled-components';
import { WebViewAnchorButton, WebViewLink } from 'shared/routing/WebViewLinks';

// Temporary fix for word break inside Button.
// Component is used in the Sidebar header.
// The bug should be investigated because
// there wasn't issue in Figma design.
const StyledLinkButton = styled(LinkButton)`
  padding-left: 14px;
  padding-right: 14px;
`;

const Progress = styled(LinearProgress)`
  height: 8px;
  border-radius: 4px;

  & > div {
    border-radius: 4px;
  }
`;

function ApplicationProgress({ value }: { value: number }) {
  const isMobile = useResponsiveValue(true, false);

  const { data: settings } = useCarrierSettings();
  const isEditingDisabled =
    !settings?.drivers_can_edit_carrier_info && MobileAppBridge.isInjected();

  return (
    <Stack>
      <Typography variant="h5">{value}% Application Complete</Typography>

      {!isMobile && <Progress variant="determinate" value={value} />}

      <Typography color="textSecondary">
        Apply to become a Verified Carrier to book loads on the Super
        Loadboard.{' '}
      </Typography>

      <WebViewLink
        href="https://support.superdispatch.com/hc/articles/35661884290707-What-is-Verified-Carrier-Status"
        target="_blank"
        rel="noopener noreferrer"
      >
        Learn More
      </WebViewLink>

      {isMobile && <Progress variant="determinate" value={value} />}

      <StyledLinkButton
        variant="primary"
        fullWidth={isMobile}
        size={isMobile ? 'small' : 'medium'}
        disabled={isEditingDisabled}
        to="/profile/verified-carrier-application/step-1?utm_medium=carrier_profile"
      >
        Become Verified Carrier
      </StyledLinkButton>
    </Stack>
  );
}

function ApplicationPending() {
  return (
    <Stack>
      {!isWebView && <PendingVerification />}

      <Typography gutterBottom={true} color="textSecondary">
        Your application is under review. Most reviews take 24-48 hours, though
        some may take longer. We'll notify you of any updates. Contacting
        support will not expedite the process.
      </Typography>

      <WebViewAnchorButton
        fullWidth={true}
        size="small"
        variant="neutral"
        target="_blank"
        href="https://support.superdispatch.com/hc/articles/35661884290707-What-is-Verified-Carrier-Status"
      >
        Learn More
      </WebViewAnchorButton>
    </Stack>
  );
}

function ApplicationReviewed() {
  const navigate = useNavigate();

  return (
    <Stack>
      {!isWebView && <Reviewed />}

      <Typography color="textSecondary">
        To expedite insurance verification, ask your agent to send a Certificate
        of Insurance (COI) listing Super Dispatch as a Certificate Holder, then
        upload the COI in Cargo Insurance.
      </Typography>

      <Button
        fullWidth={true}
        size="small"
        variant="neutral"
        onClick={() => {
          navigate(
            isWebView ? '/profile/cargo-insurance' : '/profile/cargo_insurance',
          );
        }}
      >
        View
      </Button>
    </Stack>
  );
}

function ApplicationNotVerifiable() {
  return (
    <Stack aria-label="non-verifiable details">
      <Typography color="textSecondary">
        Super Dispatch can only verify Auto Haulers, Towaway and Driveaway
        carriers based in the U.S. You can continue to book loads from the Super
        Loadboard for shippers who have approved you to request or instantly
        book.
      </Typography>

      <Button
        fullWidth={true}
        size="small"
        variant="neutral"
        onClick={() => {
          openExternalURL(
            'https://support.superdispatch.com/hc/articles/35661834835859-Why-can-t-my-documents-be-verified',
            { target: '_blank' },
          );
        }}
      >
        Learn More
      </Button>
    </Stack>
  );
}

export function VerifiedCarrierApplicationStatus({
  verified_carrier_application,
}: VerifiedCarrierApplicationStatusDTO) {
  const { progress, status } = verified_carrier_application;

  if (status === 'not_submitted') {
    return <ApplicationProgress value={progress.percentage} />;
  }

  if (status === 'pending') {
    return <ApplicationPending />;
  }

  if (status === 'reviewed') {
    return <ApplicationReviewed />;
  }

  if (status === 'revoked' && !isWebView) {
    return <Revoked />;
  }

  if (status === 'declined' && !isWebView) {
    return <Declined />;
  }

  if (status === 'non_verifiable') {
    return <ApplicationNotVerifiable />;
  }

  return null;
}
